import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Divider,
  TextField,
  Paper,
} from '@mui/material';
import {
  Delete,
  Add,
  Remove,
  ShoppingCart,
  ArrowBack,
} from '@mui/icons-material';
import { fetchCart, updateCartItem, removeFromCart, updateLocalCartItem, removeFromLocalCart } from '../../store/slices/cartSlice';
import { toast } from 'react-toastify';
import { RootState, CartItem } from '../../types';
import { AppDispatch } from '../../store/store';
import PriceDisplay from '../../components/common/PriceDisplay';
import { formatPrice } from '../../utils/currency';
import { useSettings } from '../../contexts/SettingsContext';
import analytics from '../../utils/analytics';

interface CartItemComponentProps {
  item: CartItem;
}

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
    const { items, totalAmount, loading } = useSelector((state: RootState) => state.cart);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { currencySettings } = useSettings();

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCart());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (items.length > 0) {
      analytics.viewCart(items, totalAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const handleQuantityChange = (item: CartItem, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    if (isAuthenticated) {
      dispatch(updateCartItem({ productId: item.product.id, quantity: newQuantity }));
    } else {
      // For guest users, use local cart actions
      dispatch(updateLocalCartItem({ itemId: item.id, quantity: newQuantity }));
    }
  };

  const handleRemoveItem = (item: CartItem) => {
    if (isAuthenticated) {
      dispatch(removeFromCart(item.product.id));
    } else {
      // For guest users, use local cart actions
      dispatch(removeFromLocalCart(item.id));
    }
    analytics.removeFromCart(item.product, item.quantity);
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      toast.info(t('cart.loginToCheckout'));
      navigate('/login');
      return;
    }
    analytics.beginCheckout(items, totalAmount);
    navigate('/checkout');
  };

  const handleGuestCheckout = () => {
    analytics.beginCheckout(items, totalAmount);
    navigate('/checkout/guest');
  };

  const CartItemComponent: React.FC<CartItemComponentProps> = ({ item }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={2}>
            <Box
              component="img"
              src={item.product?.imageUrl || '/placeholder-product.jpg'}
              alt={item.product?.title}
              sx={{
                width: '100%',
                height: 100,
                objectFit: 'cover',
                borderRadius: 1,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="h6">{item.product?.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {item.product?.categories?.[0]?.name}
            </Typography>
            <PriceDisplay
              price={item.product?.price || 0}
              discountPrice={item.product?.discountPrice}
              priceEur={item.product?.priceEur}
              discountPriceEur={item.product?.discountPriceEur}
              variant="body2"
              layout="stacked"
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => handleQuantityChange(item, item.quantity - 1)}
                disabled={item.quantity <= 1}
              >
                <Remove />
              </IconButton>
              <TextField
                size="small"
                value={item.quantity}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  handleQuantityChange(item, value);
                }}
                inputProps={{
                  style: { textAlign: 'center', width: 50 },
                  min: 1,
                  max: item.product?.stock || 99,
                }}
                type="number"
              />
              <IconButton
                size="small"
                onClick={() => handleQuantityChange(item, item.quantity + 1)}
                disabled={item.quantity >= (item.product?.stock || 99)}
              >
                <Add />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Box sx={{ textAlign: 'right' }}>
              <PriceDisplay
                price={(item.product?.price || 0) * item.quantity}
                priceEur={item.product?.priceEur ? item.product.priceEur * item.quantity : undefined}
                variant="h6"
                layout="stacked"
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={1}>
            <IconButton
              color="error"
              onClick={() => handleRemoveItem(item)}
              sx={{ float: 'right' }}
            >
              <Delete />
            </IconButton>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // For now, we'll allow both authenticated and guest users to view the cart
  // Guest cart will be managed via session storage

  if (items.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <ShoppingCart sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {t('cart.empty')}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {t('cart.emptyMessage')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/shop')}
            sx={{ mt: 2 }}
          >
            {t('cart.continueShopping')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('cart.title')} ({items.length} {items.length === 1 ? t('cart.item') : t('cart.items')})
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {items.map((item: CartItem) => (
            <CartItemComponent key={item.id} item={item} />
          ))}
          
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/shop')}
            sx={{ mt: 2 }}
          >
            {t('cart.continueShopping')}
          </Button>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              {t('checkout.orderSummary')}
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>{t('cart.subtotal')}:</Typography>
                <Typography>
                  {formatPrice(totalAmount, currencySettings)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>{t('cart.shipping')}:</Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography>{t('cart.calculateAtCheckout')}</Typography>
                </Box>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">{t('cart.total')}:</Typography>
              <Typography variant="h6" color="primary">
                {formatPrice(totalAmount, currencySettings)}
              </Typography>
            </Box>

            {isAuthenticated ? (
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleCheckout}
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {t('cart.proceedToCheckout')}
              </Button>
            ) : (
              <>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleCheckout}
                  disabled={loading}
                  sx={{ mb: 1 }}
                >
                  {t('cart.loginAndCheckout')}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={handleGuestCheckout}
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  {t('cart.checkoutAsGuest')}
                </Button>
              </>
            )}

            <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
              {t('cart.payOnDelivery')}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CartPage;